import React from "react";
import type { Tournament } from "../../types";

type Props = {
  tournament: Tournament | any;
  isDarkMode: boolean;
  statusLabel?: string;
  timerLabel?: string;
  scopeEmoji?: string;
  registered?: boolean;
  registrationAction?: {
    label: string;
    tone: "success" | "danger";
    disabled?: boolean;
  } | null;
  onRegistrationAction?: () => void;
  onBack: () => void;
  onClose: () => void;
  onOpenDock: () => void;
};

const text = (value: unknown) => String(value ?? "").trim();
const titleCase = (value: unknown) => text(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const formatDateTime = (value: unknown) => {
  const ms = Number(value || 0);
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
};

export default function TournamentMapDetail({
  tournament,
  isDarkMode,
  statusLabel = "Tournament",
  timerLabel = "",
  scopeEmoji = "🏆",
  registered = false,
  registrationAction = null,
  onRegistrationAction,
  onBack,
  onClose,
  onOpenDock,
}: Props) {
  if (!tournament) return null;

  const name = text(tournament?.name) || "Tournament";
  const description = text(tournament?.description);
  const worldwide = text(tournament?.locationScope).toLowerCase() === "worldwide" || text(tournament?.tournamentScale).toLowerCase() === "global";
  const location = text(tournament?.locationLabel) || text(tournament?.location) || text(tournament?.mapLocation?.label) || (worldwide ? "Worldwide" : "Tournament area");
  const coverUrl = text(tournament?.coverImageUrl || tournament?.coverThumbUrl);
  const coverX = Math.min(100, Math.max(0, Number(tournament?.coverImagePositionX ?? 50)));
  const coverY = Math.min(100, Math.max(0, Number(tournament?.coverImagePositionY ?? 50)));
  const coverZoom = Math.min(1.6, Math.max(1, Number(tournament?.coverImageZoom || 1)));

  const fee = Number(tournament?.entryFeeShells ?? tournament?.entryFee ?? tournament?.shellEntryFee ?? 0);
  const feeLabel = Number.isFinite(fee) && fee > 0 ? `${Math.round(fee)} Shells` : "Free";
  const participants = Number(tournament?.approvedParticipantCount ?? tournament?.participantCount ?? tournament?.participants?.length ?? 0);
  const maxParticipants = Number(tournament?.maxParticipants || 0);
  const participantLabel = Number.isFinite(participants)
    ? maxParticipants > 0 ? `${Math.max(0, Math.round(participants))}/${Math.round(maxParticipants)}` : `${Math.max(0, Math.round(participants))}`
    : "—";
  const modeLabel = text(tournament?.participationMode).toLowerCase() === "team" ? "Team" : "Solo";
  const scoringLabel = titleCase(tournament?.scoringMode || tournament?.competitionFormat || "Tournament");
  const prize = Number(tournament?.shellPrizePool ?? tournament?.prizePool ?? 0);
  const prizeLabel = Number.isFinite(prize) && prize > 0 ? `${Math.round(prize)} Shells` : "—";
  const eligibleSpecies = Array.isArray(tournament?.eligibleSpecies) ? tournament.eligibleSpecies.map(text).filter(Boolean) : [];
  const rulesText = text(tournament?.rulesText || tournament?.rules);
  const allowedMethods = Array.isArray(tournament?.allowedMethods) ? tournament.allowedMethods.map(text).filter(Boolean) : [];
  const disallowedMethods = Array.isArray(tournament?.disallowedMethods) ? tournament.disallowedMethods.map(text).filter(Boolean) : [];

  const statRows = [
    ["Mode", modeLabel], ["Entry", feeLabel], ["Anglers", participantLabel], ["Scoring", scoringLabel],
  ];
  const infoRows = [
    ["Starts", formatDateTime(tournament?.startDate)],
    ["Ends", formatDateTime(tournament?.endDate)],
    ["Prize pool", prizeLabel],
    ["Location", location],
  ];

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-[5450] flex min-h-0 items-center justify-center bg-black/50 px-2.5 pt-2.5 backdrop-blur-[3px] sm:px-4 sm:pt-4"
      style={{
        paddingBottom: "calc(var(--roddo-map-bottom-clearance, var(--roddo-bottom-nav-h, 84px)) + env(safe-area-inset-bottom) + 10px)",
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-full min-h-0 w-full max-w-[620px] flex-col overflow-hidden rounded-[24px] border shadow-[0_30px_90px_rgba(0,0,0,.48)]"
        style={{ background: isDarkMode ? "#0D1626" : "#FFFDF8", borderColor: isDarkMode ? "rgba(142,156,211,.20)" : "rgba(101,77,49,.14)", color: isDarkMode ? "#F2F6FC" : "#2D3957" }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${name} tournament details`}
      >
        <div className="relative shrink-0 overflow-hidden">
          {coverUrl ? (
            <div className="relative h-[clamp(132px,22dvh,190px)]">
              <img src={coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${coverX}% ${coverY}%`, transform: `scale(${coverZoom})`, transformOrigin: `${coverX}% ${coverY}%` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white sm:p-5">
                <div className="flex flex-wrap items-center gap-1.5 text-[9px] font-black uppercase tracking-[.1em]">
                  <span className="rounded-full border border-white/20 bg-black/40 px-2.5 py-1 backdrop-blur-md">{statusLabel}</span>
                  {registered ? <span className="rounded-full border border-emerald-200/30 bg-emerald-500/85 px-2.5 py-1">✓ Registered</span> : null}
                  {timerLabel ? <span className="rounded-full border border-white/20 bg-black/40 px-2.5 py-1 text-[#FFE29A] backdrop-blur-md">{timerLabel}</span> : null}
                </div>
                <div className="mt-2 text-[24px] font-black leading-tight tracking-[-.025em] drop-shadow-md sm:text-[28px]">{name}</div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-white/85"><span>{scopeEmoji}</span><span>{location}</span></div>
              </div>
            </div>
          ) : (
            <div className="border-b px-4 py-4 sm:px-5" style={{ borderColor: isDarkMode ? "rgba(142,156,211,.12)" : "rgba(101,77,49,.10)" }}>
              <div className="text-[9px] font-black uppercase tracking-[.12em]" style={{ color: isDarkMode ? "#FFE0A0" : "#A96E08" }}>{statusLabel}</div>
              <div className="mt-1 text-[24px] font-black leading-tight">{name}</div>
              <div className="mt-1 text-[11px] font-semibold opacity-60">{scopeEmoji} {location}{timerLabel ? ` · ${timerLabel}` : ""}</div>
            </div>
          )}

          <button type="button" onClick={onClose} className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-white/20 bg-black/45 text-lg font-black text-white backdrop-blur-md" aria-label="Close tournament details">×</button>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 [scrollbar-gutter:stable] sm:px-5 sm:py-5"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {statRows.map(([label, value]) => (
              <div key={label} className="rounded-[14px] border px-2.5 py-3 text-center" style={{ background: isDarkMode ? "rgba(255,255,255,.035)" : "rgba(255,255,255,.72)", borderColor: isDarkMode ? "rgba(142,156,211,.11)" : "rgba(101,77,49,.08)" }}>
                <div className="truncate text-[11px] font-black">{value}</div>
                <div className="mt-0.5 text-[8px] font-black uppercase tracking-[.08em] opacity-45">{label}</div>
              </div>
            ))}
          </div>

          {description ? <p className="mt-4 text-[12px] font-semibold leading-[1.65] opacity-80">{description}</p> : null}

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {infoRows.map(([label, value]) => (
              <div key={label} className="rounded-[14px] border px-3.5 py-3" style={{ borderColor: isDarkMode ? "rgba(142,156,211,.10)" : "rgba(101,77,49,.08)", background: isDarkMode ? "rgba(255,255,255,.025)" : "rgba(255,255,255,.56)" }}>
                <div className="text-[8px] font-black uppercase tracking-[.1em] opacity-40">{label}</div>
                <div className="mt-1 text-[11px] font-black leading-4">{value}</div>
              </div>
            ))}
          </div>

          {eligibleSpecies.length ? (
            <section className="mt-4">
              <div className="text-[9px] font-black uppercase tracking-[.12em] opacity-45">Eligible species</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {eligibleSpecies.slice(0, 12).map((species: string) => <span key={species} className="rounded-full border px-2.5 py-1 text-[10px] font-bold" style={{ borderColor: isDarkMode ? "rgba(142,156,211,.13)" : "rgba(101,77,49,.10)", background: isDarkMode ? "rgba(255,255,255,.035)" : "rgba(255,255,255,.64)" }}>{species}</span>)}
              </div>
            </section>
          ) : null}

          {rulesText ? (
            <section className="mt-4 rounded-[16px] border p-3.5" style={{ borderColor: isDarkMode ? "rgba(142,156,211,.12)" : "rgba(101,77,49,.09)", background: isDarkMode ? "rgba(255,255,255,.025)" : "rgba(255,255,255,.56)" }}>
              <div className="text-[9px] font-black uppercase tracking-[.12em] opacity-45">Rules</div>
              <div className="mt-2 whitespace-pre-line text-[11px] font-semibold leading-[1.6] opacity-80">{rulesText}</div>
            </section>
          ) : null}

          {(allowedMethods.length || disallowedMethods.length) ? (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {allowedMethods.length ? <div className="rounded-[15px] border p-3" style={{ borderColor: isDarkMode ? "rgba(105,220,156,.16)" : "rgba(47,158,88,.12)" }}><div className="text-[9px] font-black uppercase tracking-[.1em] text-emerald-500">Allowed</div><div className="mt-1.5 text-[10.5px] font-semibold leading-5 opacity-75">{allowedMethods.join(" · ")}</div></div> : null}
              {disallowedMethods.length ? <div className="rounded-[15px] border p-3" style={{ borderColor: isDarkMode ? "rgba(245,130,130,.16)" : "rgba(196,75,75,.12)" }}><div className="text-[9px] font-black uppercase tracking-[.1em] text-red-400">Not allowed</div><div className="mt-1.5 text-[10.5px] font-semibold leading-5 opacity-75">{disallowedMethods.join(" · ")}</div></div> : null}
            </div>
          ) : null}
        </div>

        <div className="shrink-0 border-t p-3.5 sm:p-4" style={{ borderColor: isDarkMode ? "rgba(142,156,211,.12)" : "rgba(101,77,49,.10)", background: isDarkMode ? "rgba(10,17,30,.98)" : "rgba(255,253,248,.98)" }}>
          <div className={`grid gap-2 ${registrationAction ? "grid-cols-2" : "grid-cols-1"}`}>
            {registrationAction ? (
              <button
                type="button"
                onClick={onRegistrationAction}
                disabled={registrationAction.disabled || !onRegistrationAction}
                className="rounded-[14px] border px-3 py-3 text-[11px] font-black transition active:scale-[.99] disabled:opacity-55"
                style={{
                  background: registrationAction.tone === "danger"
                    ? (isDarkMode ? "rgba(239,139,145,.10)" : "rgba(166,61,73,.07)")
                    : (isDarkMode ? "rgba(52,211,153,.12)" : "rgba(25,123,88,.08)"),
                  borderColor: registrationAction.tone === "danger"
                    ? (isDarkMode ? "rgba(239,139,145,.22)" : "rgba(166,61,73,.16)")
                    : (isDarkMode ? "rgba(52,211,153,.22)" : "rgba(25,123,88,.16)"),
                  color: registrationAction.tone === "danger"
                    ? (isDarkMode ? "#FF9AA5" : "#A63D49")
                    : (isDarkMode ? "#8FE0BE" : "#197B58"),
                }}
              >
                {registrationAction.label}
              </button>
            ) : null}
            <button type="button" onClick={onOpenDock} className="rounded-[14px] border px-4 py-3 text-[12px] font-black transition active:scale-[.99]" style={{ background: isDarkMode ? "#F0C86D" : "#2F80ED", borderColor: isDarkMode ? "rgba(240,200,109,.30)" : "rgba(47,128,237,.26)", color: isDarkMode ? "#1A2233" : "#FFFFFF" }}>
              Open Tournament Dock
            </button>
          </div>
          <div className="mt-2 text-center text-[9px] font-semibold opacity-45">Opens this tournament in Tournament Dock for standings, participants, and full event tools.</div>
        </div>
      </div>
    </div>
  );
}
